import { AccessControl } from '@components/layout/AccessControl'
import { useRent } from '@services/contracts/queires/useRent'
import { useOffersDetail } from '@services/offers/queries/useOffersDetail'
import { ChevronLeft, ChevronRight, CircleCheck, Clock } from 'lucide-react'
import { useEffect, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'

import RentDetail from './RentDetail'
import { EditRentFormModal } from './RentEdit'
import { RentCreateModal } from './RentCreateModal'

const TH =
  'px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground'
const TD = 'px-4 py-3.5 align-middle text-sm text-ink'

// The endpoint caps a page at 50 (LimitOffsetPagination.max_limit).
const PAGE_SIZES = [10, 20, 50]
const DEFAULT_SIZE = 10

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString('sk-SK') : '-'

const formatAmount = (value?: string) => `${Number(value ?? 0).toFixed(2)} €`

function PaidPill({ paid }: { paid?: boolean }) {
  const { t } = useTranslation('contract')
  return paid ? (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-success-soft px-2.5 py-0.5 text-xs font-semibold text-success">
      <CircleCheck className="h-3.5 w-3.5" />
      {t('rent_table.yes')}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-peach/15 px-2.5 py-0.5 text-xs font-semibold text-peach">
      <Clock className="h-3.5 w-3.5" />
      {t('rent_table.no')}
    </span>
  )
}

export function RentTable({
  offerId
}: {
  offerId?: string
  noSearch?: boolean
}) {
  const { t } = useTranslation('contract')
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(DEFAULT_SIZE)
  const query = useRent({ id: offerId, limit: size, offset: (page - 1) * size })
  const { data: offer } = useOffersDetail(offerId)
  const rents = query.data?.results ?? []
  const total = query.data?.count ?? 0
  const pages = Math.max(1, Math.ceil(total / size))

  // A deleted rent or a bigger page size can leave the page past the end.
  useEffect(() => {
    if (total > 0 && page > pages) setPage(pages)
  }, [total, page, pages])

  return (
    <ErrorBoundary fallbackRender={() => <div>Error</div>}>
      <div className="space-y-4">
        <AccessControl role="ADMIN">
          <div className="flex justify-end">
            <RentCreateModal offerId={offerId || ''} offer={offer} />
          </div>
        </AccessControl>

        {query.isLoading ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">
            …
          </div>
        ) : rents.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-ink/10 px-4 py-10 text-center text-sm text-muted-foreground">
            {t('rent_table.rents.no_actual_payments')}
          </div>
        ) : (
          <>
            {/* Mobile: stacked rows */}
            <div className="divide-y divide-ink/[0.06] sm:hidden">
              {rents.map((rent) => (
                <div key={rent.id} className="space-y-2.5 py-4">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-ink">
                      {t(`rent_table.${rent.payment_type}`)}
                    </span>
                    <PaidPill paid={rent.paid} />
                  </div>
                  <div className="font-display text-xl font-bold leading-none text-ink">
                    {formatAmount(rent.amount)}
                  </div>
                  <div className="flex flex-col gap-1 text-sm">
                    <Row
                      label={t('rent_table.due_date')}
                      value={formatDate(rent.due_date)}
                    />
                    <Row
                      label={t('rent_table.paid_date')}
                      value={formatDate(rent.paid_date)}
                    />
                    <Row
                      label={t('rent_table.variable_symbol')}
                      value={rent.variable_symbol || '-'}
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    {rent.id && (
                      <EditRentFormModal
                        rentId={rent.id}
                        initialValues={rent}
                      />
                    )}
                    {rent.id && <RentDetail rent={rent} />}
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop: table */}
            <div className="hidden overflow-x-auto sm:block">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-ink/[0.08] bg-ink/[0.02] text-left">
                    <th className={TH}>{t('rent_table.payment_type')}</th>
                    <th className={TH}>{t('rent_table.amount')}</th>
                    <th className={TH}>{t('rent_table.due_date')}</th>
                    <th className={TH}>{t('rent_table.variable_symbol')}</th>
                    <th className={TH}>{t('rent_table.specific_symbol')}</th>
                    <th className={TH}>{t('rent_table.paid')}</th>
                    <th className={TH}>{t('rent_table.paid_date')}</th>
                    <th className={cn(TH, 'text-right')}>
                      {t('rent_table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rents.map((rent) => (
                    <tr
                      key={rent.id}
                      className="border-b border-ink/[0.05] transition-colors last:border-0 hover:bg-ink/[0.015]"
                    >
                      <td className={TD}>
                        {t(`rent_table.${rent.payment_type}`)}
                      </td>
                      <td className={cn(TD, 'whitespace-nowrap font-semibold')}>
                        {formatAmount(rent.amount)}
                      </td>
                      <td className={cn(TD, 'whitespace-nowrap')}>
                        {formatDate(rent.due_date)}
                      </td>
                      <td className={TD}>{rent.variable_symbol || '-'}</td>
                      <td className={TD}>{rent.specific_symbol || '-'}</td>
                      <td className={TD}>
                        <PaidPill paid={rent.paid} />
                      </td>
                      <td className={cn(TD, 'whitespace-nowrap')}>
                        {formatDate(rent.paid_date)}
                      </td>
                      <td className={cn(TD, 'text-right')}>
                        <div className="flex items-center justify-end gap-2">
                          {rent.id && (
                            <EditRentFormModal
                              rentId={rent.id}
                              initialValues={rent}
                            />
                          )}
                          {rent.id && <RentDetail rent={rent} />}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {total > DEFAULT_SIZE && (
              <Pager
                page={page}
                pages={pages}
                size={size}
                total={total}
                onPage={setPage}
                onSize={(value) => {
                  setSize(value)
                  setPage(1)
                }}
              />
            )}
          </>
        )}
      </div>
    </ErrorBoundary>
  )
}

function Pager(props: {
  page: number
  pages: number
  size: number
  total: number
  onPage: (page: number) => void
  onSize: (size: number) => void
}) {
  const { page, pages, size, total, onPage, onSize } = props
  const { t } = useTranslation('contract')
  const button =
    'flex h-8 w-8 items-center justify-center rounded-full border-0 bg-transparent p-0 text-ink transition-colors hover:bg-ink/[0.05] disabled:opacity-30'

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-ink/[0.06] pt-4 text-sm">
      <div className="flex items-center gap-2 text-muted-foreground">
        <span>{t('rent_table.rows_per_page')}</span>
        <select
          value={size}
          onChange={(e) => onSize(Number(e.target.value))}
          className="rounded-lg border border-ink/15 bg-white px-2 py-1 text-sm text-ink outline-none focus:border-primary"
        >
          {PAGE_SIZES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <span>{t('rent_table.total', { count: total })}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="px-2 font-medium text-muted-foreground">
          {page} / {pages}
        </span>
        <button
          type="button"
          className={button}
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          type="button"
          className={button}
          disabled={page >= pages}
          onClick={() => onPage(page + 1)}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-ink">{value}</span>
    </div>
  )
}
